import accounting from "accounting"
import { Flex, Progress, Tag, Typography } from "antd"
import cls from "classnames"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"

import DeadlinePill from "../../../../../../../Components/DeadlinePill"
import InfoTooltip from "../../../../../../../Components/InfoTooltip/InfoTooltip"
import { formatCurrency } from "../../../../../../../Utils/utils"
import { TYPE_LABELS } from "../../../../AllPage/constants/statusMeta"
import { FLAG_REASON_META } from "../../../constants/recommendation"
import { titleCase } from "../../../utils/titleCase"

import groupStyles from "../../group.module.less"
import styles from "./disputeFields.module.less"

const { Text } = Typography

// Missing values render as the usual dot rather than an empty cell.
const EMPTY = "."

const bandColor = (pct) => (pct >= 67 ? "#16895A" : pct >= 34 ? "#DF7009" : "#D71313")

// The agreed field list: Dispute leads as its own card, its fields split into two
// sub-columns (identity/classification left, timeline/progress right). Below it,
// Customer (who they are and the card they used) and Transaction (incl. Shopify order
// context) sit side by side as peers. Each section is a surface card, matching the hero
// and the activity panel, so the page reads as one set of panels instead of
// hairline-divided text.
const DisputeFields = ({
  dispute,
  status,
  escalated,
  stageLabel,
  isActionable,
  autoSubmitAt,
  showPlainEnrichment
}) => {
  const { t } = useTranslation()

  const enrichment = dispute.enrichment
  const enrichmentScored =
    enrichment != null && !["won", "lost_defended", "lost_undefended", "skipped"].includes(status)

  return (
    <div className={styles.fieldgroups}>
      <div className={groupStyles.groupCard}>
        <div className={groupStyles.groupLabel}>{t("Dispute")}</div>
        <div className={styles.kvCols}>
          <div className={styles.kv}>
            {dispute.flag_reason && FLAG_REASON_META[dispute.flag_reason] ? (
              <>
                <div>{t("Why flagged")}</div>
                <div>
                  <Tag color={FLAG_REASON_META[dispute.flag_reason].color}>
                    {t(FLAG_REASON_META[dispute.flag_reason].label)}
                  </Tag>
                </div>
              </>
            ) : null}
            <div>{t("Dispute ID")}</div>
            <div>{dispute.processor_dispute_id || EMPTY}</div>
            <div>{t("Type")}</div>
            <div>{t(TYPE_LABELS[dispute.dispute_type] || TYPE_LABELS.dispute)}</div>
            <div>{t("Category")}</div>
            <div>{dispute.reason_category_label || EMPTY}</div>
            {dispute.reason_code ? (
              <>
                <div>{t("Reason code")}</div>
                <div className={styles.reasonCode}>
                  <span>
                    {dispute.reason_code}{" "}
                    <Text type="secondary">({titleCase(dispute.issuer)})</Text>
                  </span>
                  {dispute.reason_code_label ? (
                    <InfoTooltip title={dispute.reason_code_label} />
                  ) : null}
                </div>
              </>
            ) : null}
            <div>{t("Stage")}</div>
            <div>{escalated ? stageLabel : t("First cycle")}</div>
            <div>{t("Evidence provided")}</div>
            <div>{dispute.evidence_provided ? t("Yes") : t("No")}</div>
            {/* Why this dispute was given up on: the note typed into the "Stop fighting"
                confirm dialog. Only meaningful once the dispute is Skipped, and only
                shown when a reason was actually left (the field is optional). */}
            {status === "skipped" && dispute.stop_note ? (
              <>
                <div>{t("Reason for stopping")}</div>
                <div>{dispute.stop_note}</div>
              </>
            ) : null}
          </div>
          <div className={styles.kv}>
            {/* Enrichment as a plain row (the table's battery gauge) whenever the rich
                breakdown block above is not available; muted dot once resolved, like the
                table column. */}
            {showPlainEnrichment ? (
              <>
                <div>{t("Enrichment")}</div>
                <div>
                  {enrichmentScored ? (
                    <span className="dispute-enrichment-cell">
                      <Progress
                        percent={Math.max(0, Math.min(100, Math.round(enrichment)))}
                        showInfo={false}
                        strokeColor={bandColor(enrichment)}
                        className="dispute-enrich-bar"
                      />
                      <Text className="dispute-enrichment-pct">{Math.round(enrichment)}%</Text>
                    </span>
                  ) : (
                    <Text type="secondary">·</Text>
                  )}
                </div>
              </>
            ) : null}
            <div>{t("Dispute filed")}</div>
            <div>
              {dispute.dispute_created_at
                ? dayjs(dispute.dispute_created_at).format("DD MMM YYYY")
                : EMPTY}
            </div>
            <div>{t("Response due")}</div>
            <div>
              {dispute.due_by ? (
                <Flex align="center" gap={8} wrap="wrap">
                  <span>{dayjs(dispute.due_by).format("DD MMM YYYY HH:mm")}</span>
                  {/* Same urgency pill as the topbar, only while it's still actionable
                      (matches the topbar's own condition) so a resolved dispute doesn't
                      show a stale red "overdue". */}
                  {isActionable ? <DeadlinePill due_by={dispute.due_by} /> : null}
                </Flex>
              ) : (
                EMPTY
              )}
            </div>
            {autoSubmitAt ? (
              <>
                <div>{t("Auto-submission")}</div>
                <div>
                  {t("Scheduled at _date_", { date: autoSubmitAt.format("DD MMM YYYY HH:mm") })}
                </div>
              </>
            ) : null}
            <div>{t("Last update")}</div>
            <div>
              {dispute.updated_at ? dayjs(dispute.updated_at).format("DD MMM YYYY HH:mm") : EMPTY}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.fieldgroupsRow}>
        <div className={cls(groupStyles.groupCard, styles.rowCard)}>
          <div className={groupStyles.groupLabel}>{t("Customer")}</div>
          <div className={styles.kv}>
            <div>{t("Customer full name")}</div>
            <div>{dispute.customer_full_name || EMPTY}</div>
            <div>{t("Customer email")}</div>
            <div>{dispute.customer_email || EMPTY}</div>
            <div>{t("Country")}</div>
            <div>{dispute.country || EMPTY}</div>
            <div>{t("Card network")}</div>
            <div>{dispute.issuer ? titleCase(dispute.issuer) : EMPTY}</div>
            <div>{t("Card")}</div>
            <div>
              {dispute.card_last4
                ? `**** ${dispute.card_last4}${dispute.card_type ? ` (${titleCase(dispute.card_type)})` : ""}`
                : EMPTY}
            </div>
            <div>{t("Issuer")}</div>
            <div>{dispute.issuing_bank || EMPTY}</div>
          </div>
        </div>

        <div className={cls(groupStyles.groupCard, styles.rowCard)}>
          <div className={groupStyles.groupLabel}>{t("Transaction")}</div>
          <div className={styles.kv}>
            <div>{t("Transaction ID")}</div>
            <div>{dispute.processor_charge_id || EMPTY}</div>
            <div>{t("Transaction status")}</div>
            <div>{dispute.transaction_status ? titleCase(dispute.transaction_status) : EMPTY}</div>
            <div>{t("Transaction date")}</div>
            <div>
              {dispute.transaction_date
                ? dayjs(dispute.transaction_date).format("DD MMM YYYY")
                : EMPTY}
            </div>
            <div>{t("Original amount")}</div>
            <div>
              {dispute.original_amount
                ? `${accounting.formatNumber(Number(dispute.original_amount), 2, ",", ".")} ${dispute.original_currency || ""}`.trim()
                : formatCurrency(Number(dispute.amount || 0))}
            </div>
            <div>{t("Converted amount")}</div>
            <div>{`${accounting.formatNumber(Number(dispute.amount || 0), 2, ",", ".")} USD`}</div>
            {/* Order context exists only on Shopify disputes (Stripe charges have no
                order), so these rows show for Shopify only. */}
            {dispute.processor === "shopify" ? (
              <>
                <div>{t("Order ID")}</div>
                <div>{dispute.order_id || EMPTY}</div>
                <div>{t("Order status")}</div>
                <div>
                  {dispute.order_status
                    ? titleCase(String(dispute.order_status).replace(/_/g, " "))
                    : EMPTY}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DisputeFields
